import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import {
  type Api,
  getCoachContentHub,
  getCoachContentHubs,
  getContentHubAssignments,
  getContentHubItem,
  getResources,
} from "api-client";

export const contentHubKeys = {
  all: ["content-hub"] as const,
  coachHubs: () => {
    return [...contentHubKeys.all, "coach-hubs"] as const;
  },
  coachHub: (contentHubId: string, tabId?: string) => {
    if (tabId) {
      return [...contentHubKeys.all, "coach-hub", contentHubId, tabId] as const;
    }

    return [...contentHubKeys.all, "coach-hub", contentHubId] as const;
  },
  resources: () => {
    return [...contentHubKeys.all, "resources"] as const;
  },
  item: (id: string) => {
    return [...contentHubKeys.all, "item", id] as const;
  },
  assignmentRoot: (contentHubId: string) => {
    return [...contentHubKeys.all, "assignments", contentHubId] as const;
  },
  assignments: (contentHubId: string, filters: ContentHubAssignmentFilters) => {
    return [...contentHubKeys.assignmentRoot(contentHubId), filters] as const;
  },
  assignmentPage: (
    contentHubId: string,
    filters: ContentHubAssignmentFilters,
    pageIndex: number,
    pageSize: number,
  ) => {
    return [
      ...contentHubKeys.assignments(contentHubId, filters),
      "page",
      pageIndex,
      pageSize,
    ] as const;
  },
};

export type ContentHubAssignmentAthlete =
  Api.GetContentHubAssignments.Output.Athlete;

export interface ContentHubAssignmentFilters {
  query: string;
  labelIds: string[];
  partnershipTypes: ("ONLINE" | "IN_PERSON" | "HYBRID")[];
  assignment: "ALL" | "ASSIGNED" | "UNASSIGNED";
}

export function coachContentHubsQueryOptions() {
  return queryOptions({
    queryKey: contentHubKeys.coachHubs(),
    queryFn: () => {
      return getCoachContentHubs().then((res) => {
        return res.data.data;
      });
    },
  });
}

export function coachContentHubQueryOptions(
  contentHubId: string,
  tabId?: string,
) {
  return queryOptions({
    queryKey: contentHubKeys.coachHub(contentHubId, tabId),
    queryFn: () => {
      return getCoachContentHub({
        path: { contentHubId: contentHubId },
        query: { tabId: tabId },
      }).then((res) => {
        return res.data.data;
      });
    },
  });
}

export function contentHubAssignmentsQueryOptions(
  contentHubId: string,
  filters: ContentHubAssignmentFilters,
  pageIndex: number,
  pageSize: number,
) {
  return queryOptions({
    queryKey: contentHubKeys.assignmentPage(
      contentHubId,
      filters,
      pageIndex,
      pageSize,
    ),
    placeholderData: keepPreviousData,
    queryFn: () => {
      return getContentHubAssignments({
        path: { contentHubId },
        query: {
          ...filters,
          labelIds: filters.labelIds,
          partnershipTypes: filters.partnershipTypes,
          pageIndex,
          pageSize,
        },
      }).then((response) => {
        return response.data.data;
      });
    },
  });
}

export function contentHubResourcesQueryOptions() {
  return queryOptions({
    queryKey: contentHubKeys.resources(),
    queryFn: () => {
      return getResources().then((res) => {
        return res.data.data;
      });
    },
  });
}

export function contentHubItemQueryOptions(itemId: string) {
  return queryOptions({
    queryKey: contentHubKeys.item(itemId),
    queryFn: () => {
      return getContentHubItem({
        path: { itemId: itemId },
      }).then((res) => {
        return res.data.data;
      });
    },
  });
}
